import { trackError } from '@proposify/frontend-observability';
import { variableShapeSchema, type VariableNode } from 'services/entities/VariablesEntity';
import { z } from 'zod';

const customFieldDtoFieldTypeSchema = z.enum(['text', 'number', 'date', 'boolean', 'dropdown']);
export type CustomFieldDtoFieldType = z.infer<typeof customFieldDtoFieldTypeSchema>;

export const customFieldFieldTypeSchema = z.enum(['text', 'date', 'dropdown']);
export type CustomFieldFieldType = z.infer<typeof customFieldFieldTypeSchema>;

const customFieldDtoBaseSchema = z.object({
  name: z.string().min(1),
  friendlyName: z.string().min(1),
  fieldType: customFieldDtoFieldTypeSchema,
  placeholder: z.string().optional(),
  options: z.array(z.string()).min(2).optional(),
});

export const customFieldDtoSchema = customFieldDtoBaseSchema.extend({
  id: z.string().uuid(),
  accountId: z.string(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
  deletedAt: z.string().datetime().optional(),
});
export type CustomFieldDto = z.infer<typeof customFieldDtoSchema>;

export const customFieldInputDtoSchema = customFieldDtoBaseSchema;
export type CustomFieldInputDto = z.infer<typeof customFieldInputDtoSchema>;

export const assignedCustomFieldDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  friendlyName: z.string(),
  path: z.string().nullish(),
  type: customFieldDtoFieldTypeSchema,
  value: z.string().nullable().nullish(),
  options: z.array(z.string()).optional().nullable().nullish(),
  placeholder: z.string().nullish(),
  isRequired: z.boolean(),
  // Malformed shape degrades to "no shape" instead of failing the whole field's parse —
  // matches the pre-ticket tolerance when `shape` was an unrecognized, stripped key.
  shape: variableShapeSchema.nullish().catch(undefined),
});
export type AssignedCustomFieldDto = z.infer<typeof assignedCustomFieldDtoSchema>;

/** Domain model: account custom field supported in UI flows (subset of {@link CustomFieldDto}). */
export const customFieldSchema = customFieldDtoSchema.extend({
  fieldType: customFieldFieldTypeSchema,
});
export type CustomField = z.infer<typeof customFieldSchema>;

/** Domain model: custom field assigned to a document/template in UI flows (subset of {@link AssignedCustomFieldDto}). */
export const assignedCustomFieldSchema = assignedCustomFieldDtoSchema.extend({
  type: customFieldFieldTypeSchema,
});
export type AssignedCustomField = z.infer<typeof assignedCustomFieldSchema>;

export function mapAssignedCustomFieldToVariableNode(field: AssignedCustomFieldDto, includeShape = false): VariableNode | null {
  if (!field.path) {
    trackError(
      new Error('Assigned Custom Field should have a path, the path is the only way to know under what domain/folder we should place it'),
      {
        errorFrom: 'editor-remote',
        feature: 'custom-fields',
        customField: field,
      }
    );
    return null;
  }

  const value = field.value;
  return {
    name: field.name,
    friendlyName: field.friendlyName,
    type: field.type,
    value: typeof value === 'string' || typeof value === 'number' ? value : null,
    path: field.path.replace(/^All\/Documents\//, 'All/Document/'),
    variables: [],
    // Absent, not present-with-undefined-value — matches the leaf invariant at
    // VariablesEntity.ts:133-140 (Object.keys/structural equality depend on it).
    ...(includeShape && field.shape ? { shape: field.shape } : {}),
  };
}

export function mapAssignedCustomFieldsToVariableNodes(fields: AssignedCustomFieldDto[], includeShape = false): VariableNode[] {
  return fields.flatMap((field) => {
    const node = mapAssignedCustomFieldToVariableNode(field, includeShape);
    return node ? [node] : [];
  });
}

export const PermissionsDTO = z.record(z.string(), z.object({ allowed: z.boolean(), denyReasons: z.array(z.string()).optional() }));
export type UserPermissionsDTO = z.infer<typeof PermissionsDTO>;
